import React from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Mail, Lock, ChevronDown, Loader2, Eye, EyeOff } from 'lucide-react';

/**
 * Collapsible email + password form. Combined login and signup —
 * branching purely on `mode === 'login'`. Eye-toggle on password and
 * "Forgot password?" link are preserved with their original
 * data-testid values so existing tests keep passing.
 */
export const EmailPasswordForm = ({
  mode = 'login',
  open, onToggle,
  email, onEmailChange,
  password, onPasswordChange,
  showPassword, onShowPasswordToggle,
  role, onRoleChange,
  loading,
  onSubmit,
}) => (
  <details
    open={open}
    onToggle={(e) => onToggle(e.currentTarget.open)}
    className="mb-2 rounded-lg border border-slate-200 bg-white"
    data-testid="auth-email-details"
  >
    <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-700 flex items-center justify-between">
      <span className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-slate-500" />{' '}
        {mode === 'login' ? 'Email & password' : 'Sign up with email & password'}
      </span>
      <ChevronDown className="h-4 w-4 text-slate-400 transition-transform" />
    </summary>

    <form onSubmit={onSubmit} className="px-4 pb-4 pt-1 space-y-3">
      <Input
        type="email"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        placeholder="you@example.com"
        data-testid="auth-email-input"
        required
      />
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Password (min 6 chars)"
          data-testid="auth-email-password"
          className="pl-9 pr-10"
          required
          minLength={6}
        />
        <button
          type="button"
          onClick={onShowPasswordToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          data-testid="auth-password-toggle"
          tabIndex={-1}
        >
          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>

      {mode === 'register' && (
        <select
          value={role}
          onChange={(e) => onRoleChange(e.target.value)}
          data-testid="auth-email-role"
          className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm"
        >
          <option value="buyer">Buyer</option>
          <option value="tenant">Tenant</option>
          <option value="seller">Seller</option>
          <option value="agent">Estate Agent</option>
          <option value="professional">Professional / Service Partner</option>
          <option value="investor">Investor / NRI</option>
        </select>
      )}

      <Button
        type="submit"
        disabled={loading}
        data-testid="auth-email-submit"
        className="w-full bg-[#0F172A] hover:bg-[#1F2937] text-white"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : mode === 'login' ? (
          'Sign In'
        ) : (
          'Create Account'
        )}
      </Button>

      {mode === 'login' && (
        <Link
          to="/forgot-password"
          className="block text-center text-xs text-slate-500 hover:text-slate-700"
          data-testid="auth-forgot-password-link"
        >
          Forgot password?
        </Link>
      )}
    </form>
  </details>
);

export default EmailPasswordForm;
